﻿import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { enqueueSnackbar } from "notistack";
import { useCallback, useContext } from "react";
import { useTextDomainContext } from "../../app/providers";
import type { MutationRemoveUserFromTenantArgs } from "@/types/graphql";
/**
 * Custom hook to delete a user company.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{removeUserFromTenant: Function, deleting: boolean}} - The function to unlink a user from tenant and the loading state.
 */
export function useRemoveUserFromTenant(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const [removeUserFromTenant, { loading }] = useMutation<any>(
    mutations.removeUserFromTenant,
    {
      onCompleted: (response) => {
        if (response?.removeUserFromTenant?.success) {
          enqueueSnackbar(gettext("The user has been unlinked."), {
            variant: "success",
          });
        } else {
          enqueueSnackbar(gettext("Failed to unlink. ") + response.message, {
            variant: "error",
          });
        }
        onCompleted(response);
      },
      onError: (err) => {
        enqueueSnackbar("Error while unlink the user: " + err.message, {
          variant: "error",
        });
        onError(err);
      },
      errorPolicy: "all",
      refetchQueries: ["TenantsUsersList", "TenantsList"],
    },
  );

  const removeUserFromTenantRow = useCallback(
    (variables: MutationRemoveUserFromTenantArgs) => {
      removeUserFromTenant({
        variables,
      }).then();
    },
    [removeUserFromTenant],
  );

  return {
    removeUserFromTenant: removeUserFromTenantRow,
    deleting: loading,
  };
}
